import {useEffect, useState} from "react";
import { useTranslation } from "react-i18next";
import {
    FiArrowLeft,
    FiUser,
    FiMail,
    FiPhone,
    FiCreditCard,
    FiCalendar,
    FiMapPin,
    FiBook
} from "react-icons/fi";
import {AiFillDelete} from "react-icons/ai";
import {useNavigate, useParams} from "react-router-dom";
import PageHeader from "../../../components/common/pageheader/PageHeader";
import {assignSubjectsToStudent, deleteSubjectFromStudent, getStudentDetail} from "../../../services/studentsApi";
import {useAuth} from "../../../context/AuthContext";
import styles from "./StudentDetailPage.module.css";
import Loader from "../../../components/common/Loader/Loader";
import ConfirmDeleteModal from "../../../components/common/deleteModal/ConfirmDeleteModal";

const StudentDetailPage = () => {
    const { t } = useTranslation();
    const {studentNumber} = useParams();
    const navigate = useNavigate();
    const {user} = useAuth();
    // View-only roles (TEACHER) can read a student but not modify their subject assignments.
    const canManageStudents = (user?.roles || []).some((r) => ["ADMIN", "OWNER", "COORDINATOR"].includes(r));

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [subjectToDelete, setSubjectToDelete] = useState(null);

    const [saving, setSaving] = useState(false);

    const fetchDetail = async () => {
        try {
            setLoading(true);
            setError("");
            const res = await getStudentDetail(studentNumber);
            setData(res);
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.studentDetail.loadError");
            setError(msg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (studentNumber) {
            fetchDetail();
        }
    }, [studentNumber]);

    if (loading) {
        return (
            <div className={styles.container}>
                <PageHeader title={t("pages.studentDetail.title")}/>
                <Loader fullscreen/>
            </div>
        );
    }

    if (error) {
        return (
            <div className={styles.container}>
                <PageHeader title={t("pages.studentDetail.title")}/>
                <p className={styles.error}>{error}</p>
            </div>
        );
    }

    if (!data) {
        return (
            <div className={styles.container}>
                <PageHeader title={t("pages.studentDetail.title")}/>
                <p className={styles.empty}>{t("pages.studentDetail.noData")}</p>
            </div>
        );
    }

    const openDeleteModal = (subjectId) => {
        setSubjectToDelete(subjectId);
        setIsDeleteOpen(true);
    };

    const handleCancelDelete = () => {
        setIsDeleteOpen(false);
        setSubjectToDelete(null);
    };

    const handleConfirmDelete = async (e) => {
        e.preventDefault();
        setError("");
        try {
            setSaving(true);
            await deleteSubjectFromStudent(studentNumber, subjectToDelete);
            fetchDetail().then();
        } catch (err) {
            const msg = err?.response?.data?.message || err?.response?.data?.error || err.message || t("pages.studentDetail.assignError");
            setError(msg);
        } finally {
            setSaving(false);
            setIsDeleteOpen(false);
        }
    }

    const {student, subjectBases} = data;

    const goBack = () => {
        navigate("/students");
    };

    const infoFields = [
        {icon: FiCreditCard, label: t("pages.studentDetail.studentNumber"), value: student.studentNumber},
        {icon: FiUser, label: t("pages.studentDetail.firstName"), value: student.firstName},
        {icon: FiUser, label: t("pages.studentDetail.lastName"), value: student.lastName},
        {icon: FiMail, label: t("pages.studentDetail.email"), value: student.email},
        {icon: FiPhone, label: t("pages.studentDetail.phone"), value: student.phone},
        {icon: FiCreditCard, label: t("pages.studentDetail.nationalId"), value: student.nationalId},
        {icon: FiCalendar, label: t("pages.studentDetail.birthDate"), value: student.birthDate},
        {icon: FiMapPin, label: t("pages.studentDetail.address"), value: student.address},
    ];

    return (
        <div className={styles.container}>
            <PageHeader title={t("pages.studentDetail.title")} subtitle={`${student.firstName} ${student.lastName}`}/>

            <div className={styles.topBar}>
                <button type="button" onClick={goBack} className={styles.backBtn}>
                    <FiArrowLeft size={18}/>
                    <span>{t("common.back")}</span>
                </button>
            </div>

            <div className={styles.content}>
                <section className={styles.card}>
                    <div className={styles.profileBlock}>
                        <div className={styles.avatarWrap}>
              <span className={styles.avatarInitials}>
                {[student.firstName, student.lastName].map((n) => (n && n[0]) || "").join("").toUpperCase() || "?"}
              </span>
                        </div>
                        <div className={styles.profileCardText}>
                            <h2 className={styles.profileName}>{student.firstName} {student.lastName}</h2>
                            <p className={styles.profileMeta}>{t("pages.studentDetail.studentNumberShort", { number: student.studentNumber })}</p>
                        </div>
                    </div>
                    <h3 className={styles.sectionTitle}>
                        <FiUser className={styles.sectionIcon}/>
                        {t("pages.studentDetail.personalInfo")}
                    </h3>
                    <div className={styles.fieldGrid}>
                        {infoFields.map(({icon: Icon, label, value}) =>
                            value != null && value !== "" ? (
                                <div key={label} className={styles.field}>
                                    <div className={styles.fieldIcon}>
                                        <Icon size={18}/>
                                    </div>
                                    <div className={styles.fieldContent}>
                                        <span className={styles.label}>{label}</span>
                                        <span className={styles.value}>{value}</span>
                                    </div>
                                </div>
                            ) : null
                        )}
                    </div>
                </section>

                <section className={styles.card}>
                    <h3 className={styles.sectionTitle}>
                        <FiBook className={styles.sectionIcon}/>
                        {t("pages.studentDetail.subjects")}
                    </h3>
                    {subjectBases && subjectBases.length > 0 ? (
                        <ul className={styles.subjectList}>
                            {subjectBases.map((sb) => (
                                <li key={sb.id} className={styles.subjectItem}>
                                    <div className={styles.subjectHeader}>
                                        <span className={styles.subjectName}>
                                          {sb.name + " - " + sb.parentName}
                                        </span>
                                        <div className={styles.actions}>
                                            {!sb.active && (
                                                <span className={styles.subjectBadge}>{t("pages.studentDetail.inactive")}</span>
                                            )}
                                            {canManageStudents && (
                                                <button
                                                    className={styles.deleteBtn}
                                                    onClick={() => openDeleteModal(sb.id)}
                                                    disabled={saving}
                                                >
                                                    <AiFillDelete size={18}/>
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                    {sb.description && (
                                        <p className={styles.subjectDescription}>{sb.description}</p>
                                    )}
                                    {sb.topics && sb.topics.length > 0 && (
                                        <p className={styles.subjectTopics}>
                                            {t("pages.studentDetail.topics", { topics: sb.topics.join(", ") })}
                                        </p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className={styles.empty}>{t("pages.studentDetail.noSubjects")}</p>
                    )}
                </section>
            </div>
            <ConfirmDeleteModal
                isOpen={isDeleteOpen}
                onConfirm={handleConfirmDelete}
                onCancel={handleCancelDelete}
                message={t("pages.studentDetail.deleteSubjectConfirm")}
                disabled={saving}
            />
        </div>
    );
};

export default StudentDetailPage;
